/**
 * PAX — PwaInstallPrompt
 *
 * Automatyczny baner instalacji PWA. Pokazuje się TYLKO gdy:
 *  - istnieje deferredPrompt (Android/Chrome/Samsung/Edge)  → button "Zainstaluj"
 *  - albo iOS Safari                                          → instrukcja "Dodaj do ekranu głównego"
 *
 * NIE pokazuje się gdy:
 *  - aplikacja już zainstalowana (standalone mode / navigator.standalone)
 *  - użytkownik zamknął baner w ciągu ostatnich 7 dni (COOLDOWN)
 *  - jesteśmy na /login lub innym auth path
 *
 * Design: dolny slide-in card (mobile-first), na desktop jako floating card w prawym dolnym rogu.
 * Zamknięcie: X w rogu (dismiss z cooldown) LUB "Zainstaluj" (natywny prompt).
 */
import { useEffect, useState } from 'react';
import { X, Download, Share, Plus } from 'lucide-react';
import { usePwaInstall } from './usePwaInstall';
import { Button } from '@/components/ui/button';

const AUTOSHOW_DELAY_MS = 8000; // 8s — nie natrętnie zaraz po wejściu

export default function PwaInstallPrompt() {
  const { state, promptInstall, dismiss } = usePwaInstall();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (state !== 'installable' && state !== 'ios-manual') {
      setVisible(false);
      return undefined;
    }
    // Delay pierwszego pokazu żeby nie irytować podczas logowania/ładowania
    const t = setTimeout(() => setVisible(true), AUTOSHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [state]);

  if (!visible) return null;
  if (state !== 'installable' && state !== 'ios-manual') return null;

  const handleInstall = async () => {
    const result = await promptInstall();
    if (result.outcome === 'accepted' || result.outcome === 'dismissed') {
      setVisible(false);
    }
  };

  const handleClose = () => {
    dismiss();
    setVisible(false);
  };

  const isIOSVariant = state === 'ios-manual';

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[10000] animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 relative">
        <button
          type="button"
          onClick={handleClose}
          data-testid="pwa-install-prompt-close"
          aria-label="Zamknij"
          className="absolute top-2 right-2 p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="w-11 h-11 rounded-lg bg-[#2563EB] flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-lg tracking-tight">PAX</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-[#0F1F3D] leading-tight">
              Zainstaluj PAX na telefonie
            </h3>
            <p className="text-xs text-gray-500 mt-1 leading-snug">
              {isIOSVariant
                ? 'Otwórz aplikację jak natywną — bez paska przeglądarki.'
                : 'Pełna aplikacja bez paska przeglądarki, dostępna z ekranu głównego.'}
            </p>

            {isIOSVariant ? (
              <div className="mt-3 space-y-1.5 text-xs text-gray-700">
                <div className="flex items-center gap-2">
                  <Share className="w-3.5 h-3.5 text-[#2563EB] flex-shrink-0" />
                  <span>Kliknij ikonę <b>Udostępnij</b> na dole ekranu Safari</span>
                </div>
                <div className="flex items-center gap-2">
                  <Plus className="w-3.5 h-3.5 text-[#2563EB] flex-shrink-0" />
                  <span>Wybierz <b>Do ekranu początkowego</b></span>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleInstall}
                  data-testid="pwa-install-prompt-install-btn"
                  className="bg-[#2563EB] hover:bg-[#1E4FCC] text-white h-8 text-xs px-3"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Zainstaluj
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleClose}
                  className="h-8 text-xs px-3 text-gray-600"
                >
                  Później
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
